import { useRecoilCallback } from 'recoil';
import { getActiveSession } from '~/domain/session/activeSession';
import { readSelectedPatient } from '~/hooks/patient/useSelectedPatient';
import { useBumpDicomProcessorVersion } from '~/hooks/useDicomProcessorVersion';

/**
 * Runs a DICOM ingest commit inside the session's change tracker.
 *
 * `toRepository`'s patient mutators fire `tracker.markChanged`, which
 * throws outside a `command()`/`batch()`: an unwrapped ingest throws
 * part-way with no changeset emitted. Batching also coalesces the import
 * into one changeset. Every ingest commit MUST go through this helper.
 *
 * Session and selected patient are read at call time: one import call can
 * unload the patient mid-flight, and the render-captured session would be
 * the disposed one, whose tracker batches into a store nothing subscribes
 * to. Both call-time sources are cleared synchronously by the unload.
 */
export function useCommitImportToRepository(): <T>(work: () => T) => T {
    const bumpDicomProcessorVersion = useBumpDicomProcessorVersion();

    return useRecoilCallback(
        ({ snapshot }) =>
            <T>(work: () => T): T => {
                const session = getActiveSession();
                const selectedPatient = readSelectedPatient(snapshot);
                try {
                    if (session) {
                        return session.context.tracker.batch(work);
                    }
                    // attachTracker is permanent, so a tracked patient with
                    // no session is mid-teardown: fail fast rather than
                    // mutate part-way with no changeset.
                    if (selectedPatient?.isTracked) {
                        throw new Error(
                            'Cannot commit an import: the patient is tracked but ' +
                                'has no active session (mid-teardown). Retry once ' +
                                'the patient session is re-established.'
                        );
                    }
                    return work();
                } finally {
                    // Even on a partial import: a fully-skipped one emits no
                    // changeset but leaves series the UI must surface.
                    bumpDicomProcessorVersion();
                }
            },
        [bumpDicomProcessorVersion]
    );
}
